import { useImmediateMutationSystem } from "@/lib/queuedMutations";
import {
  type AgendaDataUpdater,
  type DragData,
  dragInsertPos,
  type DragInsertPos,
  inDragContainer,
  insertClasses,
  isDataForSelf,
  matchingDragType,
} from "./dragDrop";
import { useMeetingEditor } from "./MeetingEditorContext";
import type { Document } from "@repo/types";
import { useEffect, useRef, useState } from "react";
import { DragGrip, EditPencil, RemoveX, SignDocument } from "./iconButtons";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { LinkIcon, LockIcon, UnlinkIcon } from "lucide-react";
import { cn } from "@repo/shared/utils";
import { multiline } from "@repo/ui/components/utilities/multiline";
import { apiService } from "@/services/apiService";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { useGetDocument } from "@/hooks/queryHooks/documentHooks";
import { documentRepo } from "@/repositories/documentRepo";

// #region Doc Loader

/** Loaders are necessary to load the object's data and only render the actual component when it
 * finishes loading because useImmediateMutationSystem requires the data be immediately
 * available in the render function. */

export const MeetingEditorDocumentLoader = ({
  ind,
  containerId,
  updateOrder,
  documentId,
  onRemove,
  index,
}: {
  ind: number;
  containerId: string;
  updateOrder: AgendaDataUpdater;
  documentId: string;
  onRemove?: (documentId: string) => void | Promise<void>;
  index: number;
}) => {
  const { canViewConfidential } = useMeetingEditor();

  const { tenant } = useMeetingEditor();
  const { data: doc } = useGetDocument(tenant.id, documentId);
  const [url, setUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!doc) return;

    let canceled = false;
    const attachment = doc ? documentRepo.getLinkedAttachment(doc) : null;

    if (doc.confidential) {
      if (canViewConfidential) {
        apiService.v1.documentDownloadUrl(tenant.id, doc.id).then((value) => {
          if (canceled) return;

          setUrl(value.data);
          setLoaded(true);
        });
      } else {
        setUrl(null);
        setLoaded(true);
      }
    } else {
      setUrl(attachment?.url ?? "");
      setLoaded(true);
    }

    return () => {
      canceled = true;
    };
  }, [doc, tenant.id, canViewConfidential]);

  if (!doc || !loaded) return <p>...</p>;
  if (doc._deleted) return null;

  return (
    <MeetingEditorDocument
      ind={ind}
      containerId={containerId}
      updateOrder={updateOrder}
      document={doc}
      onRemove={onRemove}
      url={url}
      index={index}
    />
  );
};

// #endregion

// #region Document

export const MeetingEditorDocument = ({
  ind,
  containerId,
  updateOrder,
  document: _document,
  onRemove,
  url,
  index,
}: {
  ind: number;
  containerId: string;
  updateOrder: AgendaDataUpdater;
  document: Document;
  onRemove?: (documentId: string) => void | Promise<void>;
  url?: string | null;
  index: number;
}) => {
  const navigate = useNavigate();
  const { mutationSystem, tenant, viewOnly, openEditForm } = useMeetingEditor();

  const mutationKey = "document:" + _document.id;
  const doc = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _document
  );

  const documentId = doc.id;

  let documentTitle: React.ReactNode = doc.name;
  if (viewOnly && url) {
    documentTitle = (
      <a href={url} target="_blank" className="text-blue-500">
        {documentTitle}
      </a>
    );
  }

  // drag-and-drop data
  const containerRef = useRef<HTMLElement | null>(null);

  const dndData: DragData = {
    id: doc.id,
    ind,
    type: "document",
    container: containerId,
    updateOrder,
    index,
    overlay: (
      <div
        className="flex flex-row gap-1 pb-2"
        style={{
          width: containerRef.current?.offsetWidth,
          height: containerRef.current?.offsetHeight,
        }}
      >
        <DragGrip />
        <div className="flex-grow px-2 rounded-md">
          <div className="flex flex-row min-h-8">
            <p className="text-base mt-1 mr-2">{documentTitle}</p>
          </div>
        </div>
      </div>
    ),
  };

  const { active, over } = useDndContext();

  const isDraggingSelf = isDataForSelf(active, dndData);
  const isOverSelf = isDataForSelf(over, dndData);
  const insert: DragInsertPos = isOverSelf ? dragInsertPos(active, over) : null;
  const dndId = dndData.id + ":" + dndData.container + ":" + dndData.ind;

  const {
    attributes,
    listeners,
    setNodeRef: setDraggable,
  } = useDraggable({
    id: dndId,
    data: dndData,
    disabled: viewOnly,
  });

  const { setNodeRef: setDroppable } = useDroppable({
    id: dndId,
    data: dndData,
    disabled:
      viewOnly ||
      !matchingDragType(active, "document") ||
      !inDragContainer(active, dndData.container),
  });

  const style = {
    opacity: isDraggingSelf ? 0 : 1,
  };

  const setNodeRef = (ref: HTMLElement | null) => {
    setDraggable(ref);
    setDroppable(ref);
    containerRef.current = ref;
  };

  // handlers
  const handleEdit = useFutureCallback(() => openEditForm("document", doc));
  const handleOpenSignForm = useFutureCallback(() => {
    navigate(new AppUrlPath(tenant.id).getDocumentSignPath(doc.id));
  });
  const handleRemoveDocument = useFutureCallback(() => {
    onRemove?.(documentId);
  });

  const attachment = documentRepo.getLinkedAttachment(doc);
  const originalAttachment = documentRepo.getAttachmentByType(doc, "original");
  const showSignDocumentButton =
    !!tenant.featureFlags?.signatures &&
    !originalAttachment?.isUrl &&
    !!originalAttachment?.attachmentId &&
    originalAttachment?.extension?.toLowerCase() === "pdf";

  const icon = doc.confidential ? (
    <LockIcon className="w-4" />
  ) : !attachment?.url ? (
    <UnlinkIcon className="w-4" />
  ) : (
    <LinkIcon className="w-4" />
  );

  return (
    <div
      className={cn("dnd relative", insertClasses(insert))}
      style={style}
      ref={setNodeRef}
    >
      <div className="flex flex-row gap-1 group pb-2">
        <DragGrip
          disabled={viewOnly}
          titleNote={
            doc.confidential
              ? "Confidential"
              : !attachment?.url
                ? "No Attachment"
                : "Attachment"
          }
          icon={icon}
          {...attributes}
          {...listeners}
        />
        <div className="flex-grow px-2 hover:bg-gray-600/5 rounded-md">
          <div className="flex flex-row min-h-8 gap-x-2">
            <p className="text-base mt-1 mr-2">{documentTitle}</p>
            {!viewOnly && (
              <>
                <EditPencil title="Edit Document" onClick={handleEdit} />
                {showSignDocumentButton && (
                  <SignDocument
                    title="Prepare Sign Requests"
                    onClick={handleOpenSignForm}
                  />
                )}
                {onRemove && (
                  <RemoveX
                    type="document"
                    title="Remove Document From Item"
                    label={doc.name}
                    onClick={handleRemoveDocument}
                  />
                )}
              </>
            )}
          </div>
          {doc.description && (
            <p className="text-gray-400 text-sm pl-9">
              {multiline(doc.description)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// #endregion
